import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Calendar,
  BarChart3,
  MessageSquare,
  Settings,
  User,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { useNavigationConfig } from '@/hooks/useNavigationConfig';
import { NavigationConfigPanel } from './NavigationConfigPanel';

// Icon mapping
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  LayoutDashboard,
  Users,
  Calendar,
  BarChart3,
  MessageSquare,
  Settings,
};

export const Navigation: React.FC = () => {
  const { config, visibleItems, toggleItemVisibility, reorderItems } = useNavigationConfig();
  const [isConfigOpen, setIsConfigOpen] = useState(false);

  const handleToggleConfig = () => {
    setIsConfigOpen(!isConfigOpen);
  };

  const handleToggleVisibility = (itemId: string) => {
    toggleItemVisibility(itemId);
  };

  const handleReorder = (itemIds: string[]) => {
    reorderItems(itemIds);
  };

  return (
    <aside
      className="flex flex-col w-64 h-[calc(100vh-64px)] sticky top-[64px] border-r overflow-hidden relative"
      style={{ background: '#2d3e9f', borderColor: 'rgba(255,255,255,0.1)' }}
    >
      {/* Page Flip Container */}
      <div className="relative w-full h-full perspective-1000">
        {/* Navigation View */}
        <div
          className={cn(
            'absolute inset-0 w-full h-full transition-transform duration-500 ease-in-out preserve-3d',
            isConfigOpen && 'rotate-y-180 backface-hidden'
          )}
          style={{
            transformStyle: 'preserve-3d',
            backfaceVisibility: 'hidden',
          }}
        >
          <div className="flex flex-col h-full">
            {/* Gear Icon */}
            <div className="px-2 pt-2 pb-1 flex justify-end">
              <button
                onClick={handleToggleConfig}
                className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-all"
                title="Configure navigation"
              >
                <Settings className="w-4 h-4" />
              </button>
            </div>

            {/* Navigation Items */}
            <nav className="flex-1 overflow-y-auto pb-20 px-2 py-2">
              {visibleItems.map((item) => {
                const IconComponent = iconMap[item.iconName] || LayoutDashboard;
                return (
                  <NavLink
                    key={item.id}
                    to={item.to}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 px-4 py-3 mb-1 rounded-lg transition-all text-white/90',
                        'hover:bg-white/10',
                        isActive && 'bg-white/20 font-semibold text-white'
                      )
                    }
                  >
                    <IconComponent className="w-5 h-5" />
                    <span className="text-sm">{item.label}</span>
                  </NavLink>
                );
              })}
            </nav>

            {/* Profile Section */}
            <div
              className="p-4 border-t"
              style={{ borderColor: 'rgba(255,255,255,0.1)' }}
            >
              <button
                className="flex items-center gap-3 w-full px-4 py-3 rounded-lg transition-all text-white/90 hover:bg-white/10"
                title="User menu"
              >
                <User className="w-5 h-5" />
                <span className="text-sm">Dr. Sarah Chen</span>
              </button>
            </div>
          </div>
        </div>

        {/* Configuration Panel View */}
        <div
          className={cn(
            'absolute inset-0 w-full h-full transition-transform duration-500 ease-in-out preserve-3d rotate-y-180',
            isConfigOpen && 'rotate-y-0',
            !isConfigOpen && 'backface-hidden'
          )}
          style={{
            transformStyle: 'preserve-3d',
            backfaceVisibility: 'hidden',
          }}
        >
          <div className="flex flex-col h-full">
            {/* Gear Icon */}
            <div className="px-2 pt-2 pb-1 flex justify-end">
              <button
                onClick={handleToggleConfig}
                className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-all"
                title="Back to navigation"
              >
                <Settings className="w-4 h-4" />
              </button>
            </div>
            <NavigationConfigPanel
              items={config.items.sort((a, b) => a.order - b.order)}
              onToggleVisibility={handleToggleVisibility}
              onReorder={handleReorder}
            />
          </div>
        </div>
      </div>

    </aside>
  );
};
